import { useState } from 'react';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import Modal from '../ui/Modal';
import { submitAtlasPlanBrief } from '../../api/concierge';
import { track } from '../../utils/metaPixel';

// Replaces the external Tally hand-off — same fields Tally settled on
// (minus the delivery-format question, dropped for good this time).
// See migrations/20261020_concierge_requests_brief_details.sql.

const TRIP_LENGTH_OPTIONS = ['Weekend', '4-7 days', 'Up to 2 weeks', 'Up to 3 weeks'];
const TRAVEL_STYLE_OPTIONS = [
  'Culture & history', 'Food & restaurants', 'Nature & hiking', 'Nightlife',
  'Adventure', 'Relaxation', 'Back-pack', 'Kids-friendly', 'Road-trip',
];
const BUDGET_OPTIONS = ['Budget Friendly', 'Moderate', 'Comfortable', 'Luxury'];
const PACE_OPTIONS = ['Relaxed', 'Moderate', 'Active', 'Packed'];

const initialForm = {
  fullName: '',
  contactEmail: '',
  destination: '',
  tripLengthBand: '',
  travelDates: '',
  groupSize: '',
  travelStyle: [],
  budget: '',
  pace: '',
  mustDo: '',
  avoidList: '',
  extraNotes: '',
};

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-charcoal-500 mb-1.5">
        {label}{required && <span className="text-coral-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass = 'w-full px-4 py-3 text-sm bg-platinum-50 rounded-xl border border-platinum-200 outline-none focus:ring-2 focus:ring-coral-300 focus:bg-white placeholder:text-platinum-400 transition-all';

function PillToggle({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${
        active
          ? 'bg-coral-50 text-coral-600 border-coral-300'
          : 'bg-white text-charcoal-500 border-platinum-200 hover:border-platinum-300'
      }`}
    >
      {label}
    </button>
  );
}

export default function AtlasPlanBriefForm({ isOpen, onClose, destination: prefillDestination = '' }) {
  const [form, setForm] = useState({ ...initialForm, destination: prefillDestination });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const toggleStyle = (style) => setForm((prev) => ({
    ...prev,
    travelStyle: prev.travelStyle.includes(style)
      ? prev.travelStyle.filter((s) => s !== style)
      : [...prev.travelStyle, style],
  }));

  const canSubmit = form.fullName.trim() && form.contactEmail.trim() && form.destination.trim()
    && form.tripLengthBand && form.travelDates.trim() && form.groupSize.trim()
    && form.travelStyle.length > 0 && form.budget && form.pace;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await submitAtlasPlanBrief(form);
      track('Lead', { content_category: 'atlas_plan_brief' });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit Atlas Plan brief:', err);
      setError("That didn't go through — mind trying again in a moment?");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset after the close animation would run, so a reopen starts fresh
    // but a user who closes mid-type by accident doesn't lose anything
    // instantly — next open is what resets it.
    setTimeout(() => {
      setForm({ ...initialForm, destination: prefillDestination });
      setSubmitted(false);
      setError('');
    }, 300);
  };

  if (submitted) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Brief sent" size="sm">
        <div className="text-center py-4">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-coral-50 flex items-center justify-center">
            <Check size={26} className="text-coral-600" strokeWidth={3} />
          </div>
          <h3 className="text-xl font-black text-charcoal-500 mb-2">Got it, {form.fullName.split(' ')[0]}.</h3>
          <p className="text-sm text-charcoal-400 leading-relaxed mb-6">
            We're on it — expect your personalised Atlas Plan within 24 hours at {form.contactEmail}.
          </p>
          <button
            onClick={handleClose}
            className="px-6 py-3 rounded-xl bg-coral-700 hover:bg-coral-800 text-white font-bold text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Start your Atlas Plan brief" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h3 className="text-xl font-black text-charcoal-500 mb-1">Tell us about your trip</h3>
          <p className="text-sm text-charcoal-400">Takes about 2 minutes. No quote, no back-and-forth — just tell us the trip, we build it.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your name" required>
            <input className={inputClass} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} placeholder="Jane Doe" required />
          </Field>
          <Field label="Email" required>
            <input type="email" className={inputClass} value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="your@email.com" required />
          </Field>
        </div>

        <Field label="Where are you going?" required>
          <input className={inputClass} value={form.destination} onChange={(e) => set('destination', e.target.value)} placeholder="e.g. Lisbon, Portugal" required />
        </Field>

        <Field label="How long is your trip?" required>
          <div className="flex flex-wrap gap-2">
            {TRIP_LENGTH_OPTIONS.map((opt) => (
              <PillToggle key={opt} label={opt} active={form.tripLengthBand === opt} onClick={() => set('tripLengthBand', opt)} />
            ))}
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="When are you travelling?" required>
            <input className={inputClass} value={form.travelDates} onChange={(e) => set('travelDates', e.target.value)} placeholder="Approximate dates if undecided" required />
          </Field>
          <Field label="How many people (+ ages)?" required>
            <input className={inputClass} value={form.groupSize} onChange={(e) => set('groupSize', e.target.value)} placeholder="e.g. 2 adults" required />
          </Field>
        </div>

        <Field label="What's your travel style?" required>
          <div className="flex flex-wrap gap-2">
            {TRAVEL_STYLE_OPTIONS.map((opt) => (
              <PillToggle key={opt} label={opt} active={form.travelStyle.includes(opt)} onClick={() => toggleStyle(opt)} />
            ))}
          </div>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Budget" required>
            <div className="flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map((opt) => (
                <PillToggle key={opt} label={opt} active={form.budget === opt} onClick={() => set('budget', opt)} />
              ))}
            </div>
          </Field>
          <Field label="Pace" required>
            <div className="flex flex-wrap gap-2">
              {PACE_OPTIONS.map((opt) => (
                <PillToggle key={opt} label={opt} active={form.pace === opt} onClick={() => set('pace', opt)} />
              ))}
            </div>
          </Field>
        </div>

        <Field label="Any must-do experiences or places?">
          <textarea className={inputClass} rows={2} value={form.mustDo} onChange={(e) => set('mustDo', e.target.value)} placeholder="Optional" />
        </Field>

        <Field label="Anything you want to avoid?">
          <textarea className={inputClass} rows={2} value={form.avoidList} onChange={(e) => set('avoidList', e.target.value)} placeholder="Optional" />
        </Field>

        <Field label="Anything else we should know?">
          <textarea className={inputClass} rows={2} value={form.extraNotes} onChange={(e) => set('extraNotes', e.target.value)} placeholder="Optional" />
        </Field>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>
        )}

        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-coral-700 hover:bg-coral-800 disabled:bg-platinum-300 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-colors"
        >
          {submitting ? (
            <>
              <Sparkles size={18} className="animate-pulse" /> Sending...
            </>
          ) : (
            <>
              Send my brief <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>
    </Modal>
  );
}
